import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  UserCircle, 
  Building2, 
  Phone, 
  Mail, 
  Plus, 
  Search,
  Briefcase,
  Linkedin,
  Star,
  MoreVertical,
  Pencil,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contacts = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [contacts, setContacts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchContacts();
    fetchClients();
  }, []);

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/contacts`);
      setContacts(res.data);
    } catch (err) {
      console.error(err);
      toast.error(isRTL ? 'فشل تحميل جهات الاتصال' : 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/clients`);
      setClients(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      if (editingContact) {
        await axios.put(`${API_URL}/api/crm/contacts/${editingContact.id}`, formData);
        toast.success(isRTL ? 'تم تحديث جهة الاتصال' : 'Contact updated');
      } else {
        await axios.post(`${API_URL}/api/crm/contacts`, formData);
        toast.success(isRTL ? 'تم إضافة جهة الاتصال' : 'Contact added');
      }
      setShowDialog(false);
      setEditingContact(null);
      setFormData({});
      fetchContacts();
    } catch (err) {
      toast.error(isRTL ? 'فشل حفظ جهة الاتصال' : 'Failed to save contact');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(isRTL ? 'هل أنت متأكد من حذف جهة الاتصال هذه؟' : 'Are you sure you want to delete this contact?')) {
      try {
        await axios.delete(`${API_URL}/api/crm/contacts/${id}`);
        toast.success(isRTL ? 'تم حذف جهة الاتصال' : 'Contact deleted');
        fetchContacts();
      } catch (err) {
        toast.error(isRTL ? 'فشل حذف جهة الاتصال' : 'Failed to delete contact');
      }
    }
  };

  const openEdit = (contact) => {
    setEditingContact(contact);
    setFormData(contact);
    setShowDialog(true);
  };

  const openNew = () => {
    setEditingContact(null);
    setFormData({});
    setShowDialog(true);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || (isRTL ? 'بدون شركة' : 'No Company');
  };

  const filteredContacts = contacts.filter(c => 
    `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8">
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : ''}>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{isRTL ? 'جهات الاتصال' : 'Contacts'}</h1>
          <p className="text-muted-foreground">{isRTL ? 'إدارة جهات الاتصال والعلاقات التجارية.' : 'Manage your business contacts and relationships.'}</p>
        </div>
        <Button onClick={openNew} className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Plus className="h-4 w-4" /> {isRTL ? 'إضافة جهة اتصال' : 'Add Contact'}
        </Button>
      </div>

      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="relative flex-1 max-w-sm">
          <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4`} />
          <Input 
            placeholder={isRTL ? 'البحث عن جهات الاتصال...' : 'Search contacts...'} 
            className={isRTL ? 'pr-10 text-right' : 'pl-10'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredContacts.map((contact) => (
          <Card key={contact.id} className="group hover:border-primary/50 transition-all">
            <CardContent className="p-5">
              <div className={`flex justify-between items-start mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                    {contact.first_name?.[0]}{contact.last_name?.[0]}
                  </div>
                  <div className={isRTL ? 'text-right' : ''}>
                    <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <h3 className="font-semibold">{contact.first_name} {contact.last_name}</h3>
                      {contact.is_primary && <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />}
                    </div>
                    <p className="text-sm text-muted-foreground">{contact.job_title || (isRTL ? 'بدون منصب' : 'No title')}</p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEdit(contact)}>
                      <Pencil className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.edit')}
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(contact.id)}>
                      <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {contact.client_id && (
                <div className={`flex items-center gap-2 text-sm text-muted-foreground mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Building2 className="h-4 w-4" />
                  <span>{getClientName(contact.client_id)}</span>
                </div>
              )}

              <div className="space-y-2 text-sm">
                {contact.email && (
                  <div className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Mail className="h-4 w-4" /> {contact.email}
                  </div>
                )}
                {contact.phone && (
                  <div className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Phone className="h-4 w-4" /> {contact.phone}
                  </div>
                )}
              </div>

              <div className={`mt-4 pt-3 border-t flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Badge variant="outline" className={contact.status === 'active' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : ''}>
                  {contact.status === 'active' ? (isRTL ? 'نشط' : 'active') : contact.status}
                </Badge>
                {contact.linkedin && (
                  <a href={contact.linkedin} target="_blank" rel="noopener noreferrer">
                    <Linkedin className="h-4 w-4 text-blue-600" />
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredContacts.length === 0 && !loading && (
          <div className="col-span-full py-12 text-center border-2 border-dashed border-border rounded-xl">
            <UserCircle className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium">{isRTL ? 'لا توجد جهات اتصال' : 'No Contacts Yet'}</h3>
            <p className="text-muted-foreground mb-4">{isRTL ? 'ابدأ بإضافة أول جهة اتصال.' : 'Start by adding your first contact.'}</p>
            <Button variant="outline" onClick={openNew}>{isRTL ? 'إضافة جهة اتصال' : 'Add Contact'}</Button>
          </div>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className={`max-w-md ${isRTL ? 'text-right' : ''}`}>
          <DialogHeader>
            <DialogTitle>{editingContact ? (isRTL ? 'تعديل جهة الاتصال' : 'Edit Contact') : (isRTL ? 'إضافة جهة اتصال جديدة' : 'Add New Contact')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isRTL ? 'الاسم الأول *' : 'First Name *'}</Label>
                <Input 
                  value={formData.first_name || ''}
                  onChange={e => setFormData({...formData, first_name: e.target.value})} 
                  placeholder={isRTL ? 'أحمد' : 'John'}
                  className={isRTL ? 'text-right' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'اسم العائلة *' : 'Last Name *'}</Label>
                <Input 
                  value={formData.last_name || ''}
                  onChange={e => setFormData({...formData, last_name: e.target.value})} 
                  placeholder={isRTL ? 'محمد' : 'Doe'}
                  className={isRTL ? 'text-right' : ''}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{isRTL ? 'الشركة' : 'Company'}</Label>
              <Select value={formData.client_id || ''} onValueChange={v => setFormData({...formData, client_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder={isRTL ? 'اختر الشركة' : 'Select company'} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isRTL ? 'المنصب' : 'Job Title'}</Label>
                <Input 
                  value={formData.job_title || ''}
                  onChange={e => setFormData({...formData, job_title: e.target.value})} 
                  placeholder={isRTL ? 'مدير المبيعات' : 'Sales Manager'}
                  className={isRTL ? 'text-right' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'القسم' : 'Department'}</Label>
                <Input 
                  value={formData.department || ''}
                  onChange={e => setFormData({...formData, department: e.target.value})} 
                  placeholder={isRTL ? 'المبيعات' : 'Sales'}
                  className={isRTL ? 'text-right' : ''}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('pages.clients.email')}</Label>
              <Input 
                type="email"
                value={formData.email || ''}
                onChange={e => setFormData({...formData, email: e.target.value})} 
                placeholder="john@company.com"
                className={isRTL ? 'text-right' : ''}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.clients.phone')}</Label>
                <Input 
                  value={formData.phone || ''}
                  onChange={e => setFormData({...formData, phone: e.target.value})} 
                  placeholder="+1 234 567 8900"
                  className={isRTL ? 'text-right' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'الجوال' : 'Mobile'}</Label>
                <Input 
                  value={formData.mobile || ''}
                  onChange={e => setFormData({...formData, mobile: e.target.value})} 
                  placeholder="+1 234 567 8900"
                  className={isRTL ? 'text-right' : ''}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{isRTL ? 'رابط LinkedIn' : 'LinkedIn URL'}</Label>
              <Input 
                value={formData.linkedin || ''}
                onChange={e => setFormData({...formData, linkedin: e.target.value})} 
                placeholder="https://linkedin.com/in/..."
                className={isRTL ? 'text-right' : ''}
              />
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('pages.common.cancel')}</Button>
            <Button onClick={handleSave}>{editingContact ? (isRTL ? 'تحديث' : 'Update') : t('pages.common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contacts;
